import { Link, useRoute } from "wouter";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { FiHome, FiInfo, FiPackage, FiBriefcase, FiMail, FiX } from "react-icons/fi";

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

const MobileMenu = ({ isOpen, onClose }: MobileMenuProps) => {
  const [isHomeActive] = useRoute("/");
  const [isAboutActive] = useRoute("/about-us");
  const [isProductsActive] = useRoute("/products");
  const [isServicesActive] = useRoute("/services");
  const [isContactActive] = useRoute("/contact");

  const menuVariants = {
    closed: { 
      opacity: 0,
      x: "100%", 
      transition: { 
        duration: 0.2 
      } 
    },
    open: { 
      opacity: 1,
      x: 0, 
      transition: { 
        duration: 0.3
      } 
    }
  };

  const itemVariants = {
    closed: { 
      opacity: 0,
      x: 20 
    },
    open: { 
      opacity: 1,
      x: 0
    }
  };

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div 
          className="md:hidden fixed inset-0 z-50 bg-white"
          initial="closed"
          animate="open"
          exit="closed"
          variants={menuVariants}
        >
          <div className="flex justify-between items-center px-4 py-3 border-b">
            <div className="flex items-center">
              <img 
                src="/images/varsha-seeds-logo.png" 
                alt="Varsha Seeds Logo" 
                className="h-14 w-auto"
              />
            </div>
            
            <motion.button 
              className="text-primary focus:outline-none hover:bg-gray-100 p-2 rounded-full"
              onClick={onClose}
              aria-label="Close menu"
              whileTap={{ scale: 0.95 }}
            >
              <FiX size={24} />
            </motion.button>
          </div>
          
          <nav className="px-6 py-8">
            <ul className="space-y-6">
              <motion.li
                variants={itemVariants}
                initial="closed"
                animate="open"
                transition={{ delay: 0.1 }}
              >
                <Link href="/">
                  <div
                    className={`flex items-center space-x-3 text-lg cursor-pointer ${isHomeActive ? 'text-primary font-medium' : 'text-neutral-800'}`}
                    onClick={onClose}
                  >
                    <FiHome size={20} />
                    <span>Home</span>
                  </div>
                </Link>
              </motion.li>
              
              <motion.li
                variants={itemVariants}
                initial="closed"
                animate="open"
                transition={{ delay: 0.2 }}
              >
                <Link href="/about-us">
                  <div
                    className={`flex items-center space-x-3 text-lg cursor-pointer ${isAboutActive ? 'text-primary font-medium' : 'text-neutral-800'}`}
                    onClick={onClose}
                  >
                    <FiInfo size={20} />
                    <span>About Us</span>
                  </div>
                </Link>
              </motion.li>
              
              <motion.li
                variants={itemVariants}
                initial="closed"
                animate="open"
                transition={{ delay: 0.3 }}
              >
                <Link href="/products">
                  <div
                    className={`flex items-center space-x-3 text-lg cursor-pointer ${isProductsActive ? 'text-primary font-medium' : 'text-neutral-800'}`}
                    onClick={onClose}
                  >
                    <FiPackage size={20} />
                    <span>Products</span>
                  </div>
                </Link>
              </motion.li>
              
              <motion.li
                variants={itemVariants}
                initial="closed"
                animate="open"
                transition={{ delay: 0.4 }}
              >
                <Link href="/services">
                  <div
                    className={`flex items-center space-x-3 text-lg cursor-pointer ${isServicesActive ? 'text-primary font-medium' : 'text-neutral-800'}`}
                    onClick={onClose}
                  >
                    <FiBriefcase size={20} />
                    <span>Services</span>
                  </div>
                </Link>
              </motion.li>
              
              <motion.li
                variants={itemVariants}
                initial="closed"
                animate="open"
                transition={{ delay: 0.5 }}
              >
                <Link href="/contact">
                  <div
                    className={`flex items-center space-x-3 text-lg cursor-pointer ${isContactActive ? 'text-primary font-medium' : 'text-neutral-800'}`}
                    onClick={onClose}
                  >
                    <FiMail size={20} />
                    <span>Contact</span>
                  </div>
                </Link>
              </motion.li>
              
              <motion.li 
                className="pt-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
              >
                <Link href="/contact">
                  <Button 
                    className="w-full bg-primary hover:bg-primary/90 text-white font-medium rounded-full"
                    onClick={onClose}
                  >
                    Get a Quote
                  </Button>
                </Link>
              </motion.li>
            </ul>
          </nav>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MobileMenu;